import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import OutlookSetupGuide from "@/components/OutlookSetupGuide";
import { useCalendarData } from "./calendar/useCalendarData";
import { openExternal } from "./calendar/calendarFormat";
import { CalendarHeader, CalendarTabs } from "./calendar/CalendarHeader";
import { CalendarLoading, UpcomingPanel, RecordingsPanel, BriefsPanel, ConnectPanel } from "./calendar/CalendarPanels";

const PrioritiesSection = ({ userId }) => {
  const cal = useCalendarData(userId);
  const [isExpanded, setIsExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState("upcoming");
  const [showOutlookGuide, setShowOutlookGuide] = useState(false);
  const [expandedBrief, setExpandedBrief] = useState(null);

  const { briefs, fetchBriefs } = cal;
  useEffect(() => {
    if (activeTab === "briefs" && briefs.length === 0) fetchBriefs();
  }, [activeTab, briefs.length, fetchBriefs]);

  const outlookConnected = !!cal.outlookStatus?.connected;

  const joinMeeting = (link) => (link?.url ? openExternal(link.url) : toast.error("No meeting link available"));
  const viewRecording = (r) => {
    const url = r.share_url || r.play_url;
    return url ? openExternal(url) : toast.error("Recording not available");
  };

  const renderPanel = () => {
    if (cal.loading) return <CalendarLoading />;
    switch (activeTab) {
      case "upcoming":
        return <UpcomingPanel meetings={cal.meetings} outlookConnected={outlookConnected} busy={cal.creatingMeeting || cal.connectingOutlook} onJoin={joinMeeting} onAddAttendee={cal.addAttendee} onConnect={() => setShowOutlookGuide(true)} onCreateMeeting={cal.createMeeting} />;
      case "recordings":
        return <RecordingsPanel recordings={cal.recordings} onView={viewRecording} />;
      case "briefs":
        return <BriefsPanel briefs={cal.briefs} loading={cal.briefsLoading} outlookConnected={outlookConnected} expandedId={expandedBrief} onToggle={(id) => setExpandedBrief(expandedBrief === id ? null : id)} onConnect={cal.connectOutlook} />;
      case "connect":
        return <ConnectPanel outlookStatus={cal.outlookStatus} integrationStatus={cal.integrationStatus} connecting={cal.connectingOutlook} onConnect={cal.connectOutlook} onDisconnect={cal.disconnectOutlook} />;
      default:
        return null;
    }
  };

  return (
    <>
      <motion.div
        className="glass-card card-3d cursor-pointer overflow-hidden relative"
        onMouseEnter={() => setIsExpanded(true)}
        onMouseLeave={() => setIsExpanded(false)}
        onClick={() => setIsExpanded(!isExpanded)}
        data-testid="priorities-section"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0, height: isExpanded ? 600 : 140 }}
        transition={{ duration: 0.4, ease: "easeInOut" }}
      >
        <div className="absolute inset-0 pointer-events-none" />
        <div className="p-6 relative z-10">
          <CalendarHeader isExpanded={isExpanded} meetingCount={cal.meetings.length} creatingMeeting={cal.creatingMeeting} onRefresh={cal.fetchCalendarData} onCreateMeeting={cal.createMeeting} />

          <AnimatePresence>
            {isExpanded && <CalendarTabs activeTab={activeTab} onChange={setActiveTab} counts={{ meetings: cal.meetings.length, recordings: cal.recordings.length }} />}
          </AnimatePresence>

          <AnimatePresence>
            {isExpanded && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <ScrollArea className="pr-4" style={{ height: 420 }}>
                  {renderPanel()}
                </ScrollArea>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>

      <OutlookSetupGuide isOpen={showOutlookGuide} onClose={() => setShowOutlookGuide(false)} userId={userId} onConnected={cal.refreshAll} />
    </>
  );
};

export default PrioritiesSection;
